import React, {
  createContext,
  useContext,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useAuth } from '@clerk/clerk-react';
import { ethers } from 'ethers';
import { apiClient } from '../services/apiClient';
import { loadConfig, saveConfig } from '../config/bundlerConfig';
import { ethereumService } from '../utils/ethereum';
import { toast } from 'react-toastify';

const ProjectContext = createContext(null);

export function ProjectProvider({ children }) {
  const { getToken, isSignedIn } = useAuth();
  const getTokenRef = useRef(getToken);
  useEffect(() => {
    getTokenRef.current = getToken;
  }, [getToken]);
  const [projects, setProjects] = useState([]);
  const [currentProjectId, setCurrentProjectId] = useState(() => localStorage.getItem('currentProjectId') || '');
  const [loadingProjects, setLoadingProjects] = useState(false);
  /** Per-project row from GET /api/me/settings (Supabase user_settings). Source of truth for assigned token. */
  const [projectSettings, setProjectSettings] = useState(null);

  const apiFetch = useCallback(
    async (path, init = {}) =>
      apiClient({
        getToken: () => getTokenRef.current(),
        projectId: null,
        path,
        init,
      }),
    [],
  );

  const ensureProjectMasterWallet = useCallback(
    async (projectId) => {
      if (!projectId) return;
      await apiFetch('/api/me/master-wallet/generate', {
        method: 'POST',
        headers: { 'X-Project-Id': projectId },
      });
    },
    [apiFetch],
  );

  const refreshProjects = useCallback(async () => {
    if (!isSignedIn) return;
    setLoadingProjects(true);
    try {
      const data = await apiFetch('/api/projects');
      const list = data.projects || [];
      // Auto-create a default project for first-time users
      if (list.length === 0) {
        const created = await apiFetch('/api/projects', {
          method: 'POST',
          body: JSON.stringify({ name: 'My First Project' }),
        });
        const p = created.project;
        setProjects([p]);
        setCurrentProjectId(p.id);
        try {
          await ensureProjectMasterWallet(p.id);
        } catch (e) {
          // If this fails (e.g. backend not configured), keep project creation successful.
        }
        return;
      }
      setProjects(list);
      setCurrentProjectId((prev) => {
        if (!prev && list[0]?.id) return list[0].id;
        return prev;
      });
    } catch (e) {
      toast.error(e?.message || 'Failed to load projects');
    } finally {
      setLoadingProjects(false);
    }
  }, [isSignedIn, apiFetch, ensureProjectMasterWallet]);

  const createProject = useCallback(
    async (name, pictureUrl) => {
      const data = await apiFetch('/api/projects', {
        method: 'POST',
        body: JSON.stringify({ name, pictureUrl }),
      });
      const p = data.project;
      setProjects((prev) => [...prev, p]);
      setCurrentProjectId(p.id);
      try {
        await ensureProjectMasterWallet(p.id);
      } catch (e) {
        // Surface this later in Wallet Management (needsMasterWallet) if required.
      }
      return p;
    },
    [apiFetch, ensureProjectMasterWallet],
  );

  const deleteProject = useCallback(
    async (projectId) => {
      if (!projectId) return;
      await apiFetch(`/api/projects/${projectId}`, { method: 'DELETE' });
      setProjects((prev) => prev.filter((p) => p.id !== projectId));
      setCurrentProjectId((prev) => (prev === projectId ? '' : prev));
    },
    [apiFetch],
  );

  /** Loads server settings for the current project only (not localStorage). Mirrors token into local bundler config so EthereumService can sign txs. */
  const refreshProjectSettings = useCallback(async () => {
    if (!isSignedIn || !currentProjectId) {
      setProjectSettings(null);
      return null;
    }
    try {
      const data = await apiClient({
        getToken: () => getTokenRef.current(),
        projectId: currentProjectId,
        path: '/api/me/settings',
        init: {},
      });
      const s = data?.settings ?? null;
      setProjectSettings(s);

      const addrRaw = (s?.token_address || s?.tokenAddress || '').toString().trim();
      const addr =
        addrRaw && ethers.utils.isAddress(addrRaw) ? ethers.utils.getAddress(addrRaw) : '';

      if (addr) {
        try {
          const current = loadConfig();
          const curTok = (current?.network?.tokenAddress || '').toString().trim().toLowerCase();
          if (curTok !== addr.toLowerCase()) {
            const next = {
              ...current,
              network: { ...(current.network || {}), tokenAddress: addr },
            };
            saveConfig(next);
            ethereumService.reloadConfig().catch(() => {});
          }
        } catch {
          /* ignore */
        }
      }

      return s;
    } catch {
      setProjectSettings(null);
      return null;
    }
  }, [isSignedIn, currentProjectId]);

  const refreshProjectSettingsRef = useRef(refreshProjectSettings);
  refreshProjectSettingsRef.current = refreshProjectSettings;

  useEffect(() => {
    if (!isSignedIn) {
      setProjectSettings(null);
      return;
    }
    void refreshProjectSettingsRef.current();
  }, [isSignedIn, currentProjectId]);

  useEffect(() => {
    if (!isSignedIn) return;
    refreshProjects();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isSignedIn]);

  useEffect(() => {
    if (currentProjectId) localStorage.setItem('currentProjectId', currentProjectId);
  }, [currentProjectId]);

  const value = useMemo(
    () => ({
      projects,
      currentProjectId,
      setCurrentProjectId,
      refreshProjects,
      refreshProjectSettings,
      projectSettings,
      createProject,
      deleteProject,
      loadingProjects,
    }),
    [
      projects,
      currentProjectId,
      loadingProjects,
      refreshProjects,
      refreshProjectSettings,
      projectSettings,
      createProject,
      deleteProject,
    ],
  );

  return <ProjectContext.Provider value={value}>{children}</ProjectContext.Provider>;
}

export function useProject() {
  const ctx = useContext(ProjectContext);
  if (!ctx) throw new Error('useProject must be used within ProjectProvider');
  return ctx;
}

